import {
  all,
  call,
  delay,
  race,
  takeEvery,
  takeLatest,
  take,
  select,
  put,
  debounce,
  spawn,
} from 'redux-saga/effects';
import { downloadBlobFromUrl, processAndDownloadZip } from '../../utils';

import {
  getFiles as getFilesApi,
  getFile as getFileApi,
  getInstructionals as getInstructionalsApi,
  deleteFile as deleteFileApi,
  uploadFile as uploadFileApi,
  updateFile as updateFileApi,
  downloadFileBlob as downloadFileBlobApi,
  downloadZippedFiles as downloadZippedFilesApi,
  updateFilesAndBookmarksTags,
} from '../../services';
import {
  displayNotification,
  getTemporaryId,
  checkOnline,
  getNotification,
} from '../notifications';

import { doRequestTags, doCreateTag } from '../tags';
import {
  REFRESH_VALUES,
  CLEAR_SITE_DATA,
  setPollingActive,
  setPollingActiveDone,
} from '../application';
import {
  REFRESH_SESSIONS,
  REQUEST_FILES,
  REQUEST_INSTRUCTIONALS,
  DELETE_FILE,
  DELETE_FILE_SUCCESS,
  UPDATE_FILES,
  UPLOAD_FILES,
  DOWNLOAD_FILES,
  deleteFileSuccess,
  receiveFiles,
  refreshSessions,
  receiveInstructionals,
  clearUploaded,
  uploadFileStarted,
  uploadFileCompleted,
} from './actions';
import { getNewTags, getTagIds } from './helpers';
import { MALWARE_SCAN_STATE, SCAN_NOTIFICATION_KEYS, getMalwareScanState } from './malwareScan';
import { getFileObjects, getUploading } from './selectors';
import { getActiveSite } from '../sites';

// Polls every 3 s for the first 1.5 min, then every 10 s, and gives up after 3 min in total.
const SCAN_POLL_FAST_INTERVAL_MS = 3000;
const SCAN_POLL_FAST_ATTEMPTS = 30;
const SCAN_POLL_SLOW_INTERVAL_MS = 10_000;
const SCAN_POLL_SLOW_ATTEMPTS = 9;
const SCAN_POLL_MAX_ATTEMPTS = SCAN_POLL_FAST_ATTEMPTS + SCAN_POLL_SLOW_ATTEMPTS;

export function* doDeleteFile(action) {
  const { fileId } = action;
  const fileObject = yield select((state) => state.files.fileObjects[fileId]);

  if (!fileObject) return;

  try {
    yield call(deleteFileApi, fileId);
    yield put(deleteFileSuccess(fileId));
    yield put(displayNotification(getNotification('deleteFile', 'success')(fileObject.filename)));
    // TODO: Maybe remove call to refresh and hook into DELETE_FILE_SUCCESS to
    // remove the file in all active sessions? Downside is that pagination
    // will not be accurate.
    yield put(refreshSessions());
  } catch (error) {
    console.error('Unable to delete file:', error);
    yield call(checkOnline);
    yield put(
      displayNotification(getNotification('deleteFile', 'error')(fileObject.filename, fileId))
    );
  }
}

export function* doRequestFiles({ sessionId, query: originalQuery }) {
  const query = {
    ...originalQuery,
    page: originalQuery.page || 1,
    sortBy: originalQuery.sortBy || 'createdAt',
    order: originalQuery.order || 'desc',
  };
  if (!originalQuery.search) {
    delete query.search;
  }
  if (!originalQuery.fileType) {
    delete query.fileType;
  }

  try {
    const { values: receivedFiles, pages } = yield call(getFilesApi, query);
    yield put(receiveFiles(sessionId, receivedFiles, pages, query));
  } catch (error) {
    console.error('Unable to fetch files:', error);
    yield call(checkOnline);
    yield put(receiveFiles(sessionId, [], null, query));
    yield put(displayNotification(getNotification('getFiles', 'error')()));
  }
}

export function* doRequestInstructionals() {
  try {
    const { values: receivedFiles } = yield call(getInstructionalsApi);
    yield put(receiveInstructionals(receivedFiles));
  } catch (error) {
    console.error('Unable to fetch files:', error);
    yield call(checkOnline);
    yield put(receiveInstructionals([]));
    yield put(displayNotification(getNotification('getFiles', 'error')()));
  }
}

export function* doRefreshSessions() {
  yield put(setPollingActive('files'));

  const sessions = yield select((state) => state.files.sessions);

  yield all(
    Object.entries(sessions).map(([sessionId, { query }]) =>
      call(doRequestFiles, { sessionId, query })
    )
  );
  yield put(setPollingActiveDone('files'));
}

export function* doUploadFile(file) {
  const formData = new FormData();
  for (const key of Object.keys(file)) {
    if (Array.isArray(file[key])) {
      for (const item of file[key]) {
        formData.append(`${key}[]`, item);
      }
    } else {
      formData.append(key, file[key]);
    }
  }

  yield put(uploadFileStarted(file));
  const temporaryId = getTemporaryId();
  yield put(displayNotification(getNotification('createFile', 'pre')(file.file.name, temporaryId)));

  try {
    const createdFile = yield call(uploadFileApi, formData);
    const scanState = getMalwareScanState(createdFile?.malwareScanStatus);

    yield put(uploadFileCompleted(file, 'SUCCESS'));
    yield put(
      displayNotification(
        getNotification('createFile', SCAN_NOTIFICATION_KEYS[scanState])(
          file.file.name,
          temporaryId
        )
      )
    );

    // spawn: takeLatest(UPLOAD_FILES) cancels the running upload saga when another upload starts
    if (scanState === MALWARE_SCAN_STATE.SCANNING) {
      yield spawn(doWatchFileScan, {
        fileId: createdFile.id,
        fileName: file.file.name,
        notificationId: temporaryId,
      });
    }

    yield put(refreshSessions());
    yield delay(3000);
    const numberUploads = yield select(getUploading);
    if (numberUploads && numberUploads.length === 0) {
      yield put(clearUploaded());
    }
  } catch (error) {
    console.error('Unable to upload file:', error);
    yield call(checkOnline);
    yield put(uploadFileCompleted(file, 'ERROR'));
    yield put(
      displayNotification(getNotification('createFile', 'error')(file.file.name, temporaryId))
    );
    yield put(
      displayNotification(getNotification('createFile', 'error')(file.file.name, temporaryId))
    );
  }
}

function* pollFileScan({ fileId, fileName, notificationId }) {
  try {
    let scanState = MALWARE_SCAN_STATE.SCANNING;
    for (
      let attempt = 0;
      attempt < SCAN_POLL_MAX_ATTEMPTS && scanState === MALWARE_SCAN_STATE.SCANNING;
      attempt += 1
    ) {
      yield delay(
        attempt < SCAN_POLL_FAST_ATTEMPTS ? SCAN_POLL_FAST_INTERVAL_MS : SCAN_POLL_SLOW_INTERVAL_MS
      );
      const latestFile = yield call(getFileApi, fileId);
      scanState = getMalwareScanState(latestFile.malwareScanStatus);
    }

    const notificationKey =
      scanState === MALWARE_SCAN_STATE.SCANNING
        ? 'scanTimedOut'
        : SCAN_NOTIFICATION_KEYS[scanState];
    yield put(
      displayNotification(getNotification('createFile', notificationKey)(fileName, notificationId))
    );
    yield put(refreshSessions());
  } catch (error) {
    console.error('Unable to check malware scan status:', error);
    // Replaces the "scanning" notification, which would otherwise stay until its timeout.
    yield put(
      displayNotification(getNotification('createFile', 'scanTimedOut')(fileName, notificationId))
    );
  }
}

// Polling stops when the user switches site or the watched file is deleted, so it never reports
// into another site or about a file that no longer exists.
export function* doWatchFileScan(watchedFile) {
  yield race({
    scanFinished: call(pollFileScan, watchedFile),
    siteCleared: take(CLEAR_SITE_DATA),
    fileDeleted: take(
      (action) => action.type === DELETE_FILE_SUCCESS && action.fileId === watchedFile.fileId
    ),
  });
}

export function* doUploadFiles(action) {
  const { siteId, files, org } = action;
  try {
    const localTags = yield select((state) =>
      state.tags.tags.filter((t) => t.site === siteId && t.name === t.id)
    );
    const newUniqueTags = getNewTags(localTags, files);

    if (newUniqueTags.length > 0) {
      yield all(
        newUniqueTags.map((tagName) =>
          call(doCreateTag, {
            org,
            site: siteId,
            name: tagName,
            categories: ['files'],
          })
        )
      );
      yield call(doRequestTags, { query: { site: siteId } });
    }

    const updatedTags = yield select((state) => state.tags.tags.filter((t) => t.site === siteId));

    yield all(
      files.map((file) => call(doUploadFile, { ...file, tags: getTagIds(updatedTags, file.tags) }))
    );
  } catch (error) {
    // individual tag creation / file upload failures are handled, this should catch
    // and display upload failure based on tag failure.
    console.error('Unable to upload files:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('filesCreateTag', 'error')('upload')));
  }
}

export function* doUpdateFile(file) {
  yield put(displayNotification(getNotification('updateFile', 'pre')(file.file.name, file.id)));

  try {
    yield call(updateFileApi, file.id, file);
    yield put(
      displayNotification(getNotification('updateFile', 'success')(file.file.name, file.id))
    );
    // TODO: This is in theory not necessary, we could just update the updated
    // file in all active sessions.
    yield put(refreshSessions());
  } catch (error) {
    console.error('Unable to update file:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateFile', 'error')(file.file.name, file.id)));
  }
}

export function* doUpdateFiles(action) {
  const { siteId, files, org } = action;

  try {
    const localTags = yield select((state) =>
      state.tags.tags.filter((t) => t.site === siteId && t.name === t.id)
    );
    const newUniqueTags = getNewTags(localTags, files);
    if (newUniqueTags.length > 0) {
      yield all(
        newUniqueTags.map((tagName) =>
          call(doCreateTag, {
            org,
            site: siteId,
            name: tagName,
            categories: ['files'],
          })
        )
      );
      yield call(doRequestTags, { query: { site: siteId } });
    }

    const updatedTags = yield select((state) => state.tags.tags.filter((t) => t.site === siteId));
    yield all(
      files.map((file) => call(doUpdateFile, { ...file, tags: getTagIds(updatedTags, file.tags) }))
    );
  } catch (error) {
    // individual tag creation / file upload failures are handled, this should catch
    // and display upload failure based on tag failure.
    console.error('Unable to update files:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('filesCreateTag', 'error')('update')));
  }
}

export function* doUpdateFilesAndBookmarksTags(action) {
  const { id: tagId, site, org } = action;
  if (!tagId) yield put(displayNotification(getNotification('deleteTag', 'warning')()));

  try {
    yield call(updateFilesAndBookmarksTags, { tagId, site, org });
    yield put(refreshSessions());
  } catch (error) {
    yield call(checkOnline);
    console.error('Unable to update files or bookmarks tags', error);
  }
}

// Helper to normalize input for doDownLoadFiles
function normalizeFilesInput(fileOrFiles) {
  if (Array.isArray(fileOrFiles)) {
    return fileOrFiles;
  }
  if (typeof fileOrFiles === 'object' && fileOrFiles !== null) {
    return [fileOrFiles];
  }
  return [];
}

function* doDownLoadFiles(action) {
  try {
    const requestedFiles = normalizeFilesInput(action.fileOrFiles);
    const { filename } = action;
    const site = yield select(getActiveSite);
    const storedFiles = yield select(getFileObjects);

    // The single-row download in TfrPanel sends only { id, filename } without malwareScanStatus.
    // For those, use the status of the file with the same id in the store. If neither has a
    // status, the file counts as unverified and is not downloaded.
    const getRequestedScanState = (requestedFile) =>
      getMalwareScanState(
        requestedFile.malwareScanStatus ?? storedFiles[requestedFile.id]?.malwareScanStatus
      );
    const files = requestedFiles.filter(
      (requestedFile) => getRequestedScanState(requestedFile) === MALWARE_SCAN_STATE.CLEAN
    );
    const blockedFiles = requestedFiles.filter(
      (requestedFile) => getRequestedScanState(requestedFile) !== MALWARE_SCAN_STATE.CLEAN
    );

    for (const blockedState of new Set(blockedFiles.map(getRequestedScanState))) {
      const blockedFileNames = blockedFiles
        .filter((blockedFile) => getRequestedScanState(blockedFile) === blockedState)
        .map((blockedFile) => blockedFile.filename || blockedFile.id)
        .join(', ');

      yield put(
        displayNotification(
          getNotification('downloadFile', 'blocked')(blockedState, blockedFileNames)
        )
      );
    }
    if (blockedFiles.length > 0) {
      yield put(refreshSessions());
    }

    if (files.length > 1) {
      // Zipped download
      const fileIds = files.map((f) => f.id);
      const blob = yield call(downloadZippedFilesApi, {
        file: fileIds,
        site: site?.id,
        org: site?.org,
      });
      const failedFiles = yield call(processAndDownloadZip, blob, filename || 'download.zip');

      failedFiles?.length > 0
        ? yield put(
            displayNotification(
              getNotification('getZippedFiles', 'partialSuccess')(
                failedFiles?.length,
                failedFiles
                  .map((nfId) => {
                    const file = files.find(({ id }) => id === nfId);
                    return file && file.filename;
                  })
                  .join(', ')
              )
            )
          )
        : yield put(
            displayNotification(getNotification('downloadFile', 'success')('Download complete'))
          );
    } else if (files.length === 1) {
      const file = files[0];
      const { id } = file;
      const fileName = file.filename || filename;
      const blob = yield call(downloadFileBlobApi, id);
      yield call(downloadBlobFromUrl, blob, fileName);
      yield put(
        displayNotification(getNotification('downloadFile', 'success')('Download complete'))
      );
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('downloadFile', 'error')()));
  }
}

export const sagas = [
  takeEvery(REQUEST_FILES, doRequestFiles),
  takeEvery(REQUEST_INSTRUCTIONALS, doRequestInstructionals),
  takeEvery(DELETE_FILE, doDeleteFile),
  takeLatest(UPLOAD_FILES, doUploadFiles),
  takeLatest(UPDATE_FILES, doUpdateFiles),
  takeEvery(DOWNLOAD_FILES, doDownLoadFiles),
  takeLatest(REFRESH_SESSIONS, doRefreshSessions),
  debounce(500, REFRESH_VALUES, doRefreshSessions),
];
